import { Link, useNavigate } from "react-router-dom";
import { AiHistoryDialog } from "./ai-history";
import { AgentQuestion } from "./agent-question";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ReferenceComposer,
  type ReferenceComposerHandle,
  type ChatReference,
  type MentionAnchor,
} from "./reference-composer";
import {
  ReferencePicker,
  type ReferencePickerHandle,
} from "./reference-picker";
import { createPortal } from "react-dom";
import { PermissionSelector, type PermissionMode } from "./permission-selector";
import { ChatSettings } from "./chat-settings";
import { useRealtimeAi } from "@/hooks/use-realtime-ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookMarked,
  Bot,
  Check,
  ChevronDown,
  CircleStop,
  Loader2,
  Lock,
  MessageSquare,
  Plus,
  Send,
  Settings,
  History as HistoryIcon,
  Trash2,
  X,
  MoreHorizontal,
  Pencil,
  ChevronRight,
} from "lucide-react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { WorkPlanningPanel } from "./work-planning";
import { localCalendarDate } from "./planning-date";
import { ViewBadge, type ContextView } from "./view-context-picker";
import { ChatPrivacyFields } from "./chat-privacy-fields";
import { useAuthStore } from "@/stores/auth-store";
import { chatDraftKey, saveChatDraft, useChatDraft } from "./chat-draft";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Conversation {
  scope?: "PROJECT" | "PERSONAL";
  createdById: string;
  id: string;
  title: string | null;
  visibility: "PRIVATE" | "SHARED" | "PROJECT";
  version: number;
  canSend?: boolean;
  canManage?: boolean;
  members?: { userId: string }[];
  discordPermissionMode?: PermissionMode;
}
interface Step {
  id: string;
  type: string;
  capability: string | null;
  status: string;
  input: Record<string, unknown> | null;
  output: Record<string, unknown> | null;
  error: { message?: string } | null;
  startedAt: string;
}
interface Proposal {
  createdAt: string;
  id: string;
  title: string;
  status: string;
  summary: {
    count?: number;
    destructive?: boolean;
    rolledBack?: boolean;
    failure?: { message: string; failedIndex: number };
    privateSourceDisclosure?: boolean;
    privateSources?: { id: string; name: string }[];
    previews?: {
      entityTitle?: string;
      before: Record<string, unknown> | null;
      after: Record<string, unknown>;
      stale: boolean;
    }[];
  };
  operations: { id: string; payload: { capability: string; input: unknown } }[];
}
interface Run {
  trigger?: string;
  id: string;
  status: string;
  triggeredById: string;
  createdAt: string;
  finishedAt?: string | null;
  promptTokens: number;
  completionTokens: number;
  steps: Step[];
  changeSets: Proposal[];
  error: { message?: string } | null;
}
interface Message {
  id: string;
  authorType: string;
  content: string;
  createdAt: string;
  agentRunId: string | null;
  data: Record<string, unknown>;
}
interface History {
  workflows?: {
    id: string;
    goal: string;
    counts: {
      total: number;
      pending: number;
      proposed: number;
      committed: number;
      excluded: number;
    };
  }[];
  conversation: Conversation;
  messages: Message[];
  runs: Run[];
  usage?: {
    inputTokens: number;
    outputTokens: number;
    cachedInputTokens: number;
  };
}
interface Providers {
  serverModels: string[];
  sharedModels?: {
    id: string;
    modelId: string;
    connectionLabel: string;
    sharedById: string;
    sharedByName: string;
  }[];
  connections: {
    id: string;
    label: string;
    ownerUserId: string | null;
    baseUrl: string;
    provider?: string;
    enabled: boolean;
    keyHint: string | null;
    modelConfigs: { id: string; modelId: string; enabled: boolean }[];
  }[];
}
interface References {
  tasks: { id: string; title: string; status: string; priority: string }[];
  views: ContextView[];
  documents: { id: string; name: string; isShared: boolean }[];
  members: { userId: string; name: string }[];
}
const activeStatuses = new Set([
  "QUEUED",
  "RUNNING",
  "WAITING_FOR_APPROVAL",
  "WAITING_FOR_INPUT",
]);
const labels = {
  fr: {
    chats: "Conversations",
    newChat: "Nouvelle conversation",
    empty: "Un assistant pour votre projet",
    intro:
      "Posez une question, créez une tâche ou préparez un plan. Les actions apparaissent ici au fur et à mesure.",
    placeholder: "Que souhaitez-vous faire dans ce projet ?",
    send: "Envoyer",
    stop: "Arrêter",
    private: "Privée",
    shared: "Membres choisis",
    project: "Tout le projet",
    share: "Partager",
    settings: "Modèles et connexions",
    model: "Modèle",
    apply: "Appliquer",
    approval: "Votre accord est nécessaire",
    destructive: "Cette proposition contient une action destructive.",
    readOnly:
      "Vous pouvez lire cette conversation. Votre rôle ne permet pas d’envoyer des messages.",
    attach: "Documents",
    consent:
      "Les documents sélectionnés seront lus par l’IA. Leur contenu et les réponses qui en sont issues seront visibles par tous les membres de cette conversation, y compris dans les détails des actions.",
    noModel: "Ajoutez une connexion pour commencer.",
    loading: "Chargement…",
    retry: "Réessayer",
    title: "Titre",
    save: "Enregistrer",
    key: "Clé API",
    models: "Identifiants de modèles (séparés par des virgules)",
    delete: "Supprimer la conversation",
    confirmDelete: "Supprimer cette conversation et ses messages ?",
    details: "Détails",
    proposal: "Proposition",
    completed: "Terminé",
    running: "En cours",
    failed: "Échec",
    cancelled: "Annulé",
    openTask: "Ouvrir la tâche",
    preview: "Aperçu des modifications",
    documentEdit: "Modification proposée du document",
    openEditor: "Ouvrir dans l’éditeur",
    undo: "Préparer une annulation",
    tokens: "tokens",
    consentTitle: "Autoriser la lecture et la divulgation dans ce chat",
    deny: "Refuser",
    cache: "en cache",
    context: "Contexte de la vue",
    you: "Vous",
  },
  en: {
    chats: "Conversations",
    newChat: "New conversation",
    empty: "An assistant for your project",
    intro:
      "Ask a question, create a task or prepare a plan. Actions appear here as they happen.",
    placeholder: "What would you like to do in this project?",
    send: "Send",
    stop: "Stop",
    private: "Private",
    shared: "Selected members",
    project: "Whole project",
    share: "Share",
    settings: "Models and connections",
    model: "Model",
    apply: "Apply",
    approval: "Your approval is needed",
    destructive: "This proposal includes a destructive action.",
    readOnly:
      "You can read this conversation. Your role does not allow sending messages.",
    attach: "Documents",
    consent:
      "Selected documents will be read by the AI. Their content and derived answers will be visible to everyone in this conversation, including action details.",
    noModel: "Add a connection to get started.",
    loading: "Loading…",
    retry: "Retry",
    title: "Title",
    save: "Save",
    key: "API key",
    models: "Model IDs (comma separated)",
    delete: "Delete conversation",
    confirmDelete: "Delete this conversation and its messages?",
    details: "Details",
    proposal: "Proposal",
    completed: "Completed",
    running: "Running",
    failed: "Failed",
    cancelled: "Cancelled",
    openTask: "Open task",
    preview: "Preview changes",
    documentEdit: "Proposed document edit",
    openEditor: "Open in editor",
    undo: "Preview undo",
    tokens: "tokens",
    consentTitle: "Allow reading and disclosure in this chat",
    deny: "Deny",
    cache: "cached",
    context: "View context",
    you: "You",
  },
};
function useText() {
  return labels[useI18n().language];
}
function useConversations(projectId: string) {
  return useQuery({
    queryKey: ["ai-conversations", projectId],
    queryFn: () =>
      apiGet<{ conversations: Conversation[] }>(
        projectId
          ? `/ai/projects/${projectId}/conversations`
          : `/ai/assistant/conversations`,
      ),
    refetchInterval: 10000,
  });
}

export function AIConversationList({
  projectId,
  activeId,
  onSelect,
  canCreate,
}: {
  projectId: string;
  activeId: string | null;
  onSelect(id: string | null): void;
  canCreate: boolean;
}) {
  const text = useText();
  const client = useQueryClient();
  const query = useConversations(projectId);
  const userId = useAuthStore((state) => state.user?.id);
  const [creationOpen, setCreationOpen] = useState(false);
  const [editing, setEditing] = useState<Conversation | null>(null);
  const references = useQuery({
    queryKey: ["ai-references", projectId],
    queryFn: () =>
      apiGet<References>(
        projectId
          ? `/ai/projects/${projectId}/references`
          : `/ai/assistant/references`,
      ),
    enabled: canCreate,
  });
  const refresh = () => {
    void client.invalidateQueries({
      queryKey: ["ai-conversations", projectId],
    });
    void client.invalidateQueries({ queryKey: ["ai-chat"] });
  };
  const create = useMutation({
    mutationFn: () =>
      apiPost<{ conversation: Conversation }>(
        projectId
          ? `/ai/projects/${projectId}/conversations`
          : "/ai/assistant/conversations",
        {},
      ),
    onSuccess: ({ conversation }) => {
      onSelect(conversation.id);
      refresh();
    },
  });
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-xs font-medium text-foreground-muted uppercase tracking-wider">
          {text.chats}
        </span>
        {canCreate && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            aria-label={text.newChat}
            disabled={create.isPending}
            onClick={() => projectId ? setCreationOpen(true) : create.mutate()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>
      {query.isLoading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-foreground-muted" />
        </div>
      )}
      {query.error && (
        <p role="alert" className="px-3 text-xs text-destructive">
          {query.error.message}
        </p>
      )}
      {create.error && (
        <p role="alert" className="px-3 text-xs text-destructive">
          {create.error.message}
        </p>
      )}
      <div className="space-y-2 px-1">
        {!projectId
          ? query.data?.conversations.map((chat) => (
              <ConversationRow
                key={chat.id}
                chat={chat}
                active={activeId === chat.id}
                onSelect={() => onSelect(chat.id)}
                canManage={chat.createdById === userId}
                onEdit={() => setEditing(chat)}
              />
            ))
          : (["PRIVATE", "SHARED", "PROJECT"] as const).map((visibility) => (
              <ConversationGroup
                key={visibility}
                title={
                  visibility === "PRIVATE"
                    ? text.private
                    : visibility === "SHARED"
                      ? text.shared
                      : text.project
                }
                chats={
                  query.data?.conversations.filter(
                    (chat) => chat.visibility === visibility,
                  ) ?? []
                }
                activeId={activeId}
                onSelect={onSelect}
                userId={canCreate ? userId : undefined}
                onEdit={setEditing}
              />
            ))}
      </div>
      {creationOpen && projectId && (
        <ProjectChatCreationDialog projectId={projectId} references={references.data} onClose={() => setCreationOpen(false)} onCreated={(id) => { onSelect(id); refresh(); }} />
      )}
      {editing && (
        <SharingDialog
          key={`${editing.id}:${editing.version}`}
          open
          onClose={() => setEditing(null)}
          chat={editing}
          references={references.data}
          onSaved={refresh}
          onRemoved={() => {
            if (activeId === editing.id) onSelect(null);
          }}
        />
      )}
    </div>
  );
}
function ConversationGroup({
  title,
  chats,
  activeId,
  onSelect,
  userId,
  onEdit,
}: {
  title: string;
  chats: Conversation[];
  activeId: string | null;
  onSelect(id: string): void;
  userId?: string;
  onEdit(chat: Conversation): void;
}) {
  const [expanded, setExpanded] = useState(true);
  if (!chats.length) return null;
  return (
    <div className="space-y-1">
      <button
        className="flex items-center gap-2 w-full px-2 py-1.5 text-xs font-medium text-foreground-muted uppercase tracking-wider hover:bg-surface-hover rounded-lg transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        <span className="flex-1 text-left">{title}</span>
        <span className="text-foreground-muted/60">{chats.length}</span>
      </button>
      {expanded && (
        <div className="space-y-0.5 pl-2">
          {chats.map((chat) => (
            <ConversationRow
              key={chat.id}
              chat={chat}
              active={activeId === chat.id}
              onSelect={() => onSelect(chat.id)}
              canManage={chat.createdById === userId}
              onEdit={() => onEdit(chat)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
function ConversationRow({
  chat,
  active,
  onSelect,
  canManage,
  onEdit,
}: {
  chat: Conversation;
  active: boolean;
  onSelect(): void;
  canManage: boolean;
  onEdit(): void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const fr = useI18n().language === "fr";
  return (
    <div
      className={cn(
        "group flex items-center justify-between px-2 py-1.5 rounded-lg cursor-pointer transition-colors",
        active
          ? "bg-primary/10 text-primary"
          : "hover:bg-surface-hover text-foreground-secondary",
      )}
      onClick={onSelect}
      onContextMenu={(event) => {
        if (canManage) {
          event.preventDefault();
          setMenuOpen(true);
        }
      }}
    >
      <button className="flex items-center gap-2 min-w-0 flex-1 text-left">
        {chat.scope !== "PERSONAL" && chat.visibility === "PRIVATE" ? (
          <Lock className="h-3.5 w-3.5 shrink-0 text-foreground-muted" />
        ) : (
          <MessageSquare className="h-3.5 w-3.5 shrink-0 text-foreground-muted" />
        )}
        <span className="text-sm truncate">
          {chat.title || (fr ? "Nouvelle conversation" : "New conversation")}
        </span>
      </button>
      {canManage && (
        <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger
            asChild
            onClick={(event) => event.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="icon"
              aria-label={
                fr ? "Options de la conversation" : "Conversation options"
              }
              className="h-6 w-6 flex-shrink-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            onClick={(event) => event.stopPropagation()}
          >
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil className="h-4 w-4 mr-2" />
              {chat.scope === "PERSONAL"
                ? fr
                  ? "Renommer"
                  : "Rename"
                : fr
                  ? "Modifier et partager"
                  : "Edit and share"}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={onEdit}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {fr ? "Supprimer…" : "Delete…"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
export function AIChat({
  projectId,
  conversationId,
  onSelect,
  context,
  userId,
  canCreate,
  canManage,
  onTask,
  onDocumentEdit,
  onView,
  initialPrompt,
  onConsumePrompt,
}: {
  projectId: string;
  conversationId: string | null;
  onSelect(id: string): void;
  context: Record<string, unknown>;
  userId: string;
  canCreate: boolean;
  canManage: boolean;
  onConsumePrompt?(): void;
  initialPrompt?: { nonce: number; text: string; documentIds?: string[] };
  onTask(id: string): void;
  onView(viewId: string): void;
  onDocumentEdit(viewId: string, proposal: Record<string, unknown>): void;
}) {
  const text = useText();
  const { language } = useI18n();
  const client = useQueryClient();
  const navigate = useNavigate();
  const draftKey = chatDraftKey(userId, projectId, conversationId);
  const {
    content: draft,
    documentIds,
    viewIds,
    update: updateDraft,
  } = useChatDraft(draftKey);
  const setDraft = (content: string) => updateDraft({ content });
  const setDocumentIds = (
    value: string[] | ((previous: string[]) => string[]),
  ) =>
    updateDraft((previous) => ({
      documentIds:
        typeof value === "function" ? value(previous.documentIds) : value,
    }));
  const setViewIds = (value: string[] | ((previous: string[]) => string[])) =>
    updateDraft((previous) => ({
      viewIds: typeof value === "function" ? value(previous.viewIds) : value,
    }));
  useRealtimeAi(projectId);
  const composer = useRef<ReferenceComposerHandle>(null);
  const inlinePicker = useRef<ReferencePickerHandle>(null);
  const [mentionAnchor, setMentionAnchor] = useState<MentionAnchor | null>(
    null,
  );
  const [permissionMode, setPermissionMode] = useState<PermissionMode>(
    () =>
      (localStorage.getItem(
        `taskflow:ai:permissions:${userId}`,
      ) as PermissionMode) || "ASK",
  );
  const permissionSave = useMutation({
    mutationFn: ({ id, version, mode }: { id: string; version: number; mode: PermissionMode }) => apiPatch<{ conversation: Conversation }>(`/ai/conversations/${id}`, { expectedVersion: version, permissionMode: mode }),
    onSuccess: (result: { conversation: Conversation }, variables) => { client.setQueryData<History>(["ai-chat", variables.id], previous => previous ? { ...previous, conversation: { ...previous.conversation, ...result.conversation } } : previous); void client.invalidateQueries({ queryKey: ["ai-chat"] }); },
    onError: (error) => { setError(error.message); void client.invalidateQueries({ queryKey: ["ai-chat"] }); },
  });
  const changePermission = (value: PermissionMode) => {
    setPermissionMode(value);
    localStorage.setItem(`taskflow:ai:permissions:${userId}`, value);
    if (!projectId && history.data?.conversation) permissionSave.mutate({ id: conversationId!, version: history.data.conversation.version, mode: value });
  };
  const [settingsTab, setSettingsTab] = useState("models");
  const [privateReference, setPrivateReference] =
    useState<ChatReference | null>(null);
  const [grantRequest, setGrantRequest] = useState<{
    runId: string;
    viewId: string;
    name: string;
  } | null>(null);
  const [referencesOpen, setReferencesOpen] = useState(false);
  const [referenceSearch, setReferenceSearch] = useState("");
  const consumedPrompt = useRef(0);
  const [modelId, setModelId] = useState(
    () => localStorage.getItem("taskflow:ai:model") || "",
  );
  const [memoryDraft, setMemoryDraft] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [contextPickerOpen, setContextPickerOpen] = useState(false);
  const [error, setError] = useState("");
  const [creationOpen, setCreationOpen] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const requestId = useRef<string | null>(null);
  const pendingSend = useRef<{
    content: string;
    documentIds: string[];
    requestId: string;
    draftKey: string;
  } | null>(null);
  const providers = useQuery({
    queryKey: ["ai-project-models", projectId],
    queryFn: () =>
      apiGet<Providers>(
        projectId ? `/ai/projects/${projectId}/models` : `/ai/providers`,
      ),
    enabled: canCreate,
  });
  const references = useQuery({
    queryKey: ["ai-references", projectId],
    queryFn: () =>
      apiGet<References>(
        projectId
          ? `/ai/projects/${projectId}/references`
          : `/ai/assistant/references`,
      ),
    enabled: canCreate,
  });
  const history = useQuery({
    queryKey: ["ai-chat", conversationId],
    queryFn: () => apiGet<History>(`/ai/conversations/${conversationId}`),
    enabled: !!conversationId,
    refetchInterval: (query) =>
      query.state.data?.runs.some((run) =>
        ["RUNNING", "QUEUED"].includes(run.status),
      )
        ? 700
        : 2500,
  });
  const mentionQuery = useQuery({
    queryKey: ["ai-reference-search", projectId, referenceSearch],
    queryFn: () =>
      apiGet<References>(
        `${projectId ? `/ai/projects/${projectId}` : "/ai/assistant"}/references?${new URLSearchParams({ text: referenceSearch })}`,
      ),
    enabled: (referencesOpen || contextPickerOpen) && canCreate,
  });
  const chat = history.data?.conversation;
  useEffect(() => {
    if (!projectId && chat?.discordPermissionMode && !permissionSave.isPending) setPermissionMode(chat.discordPermissionMode);
  }, [projectId, chat?.id, chat?.discordPermissionMode, permissionSave.isPending]);
  const committedChanges =
    history.data?.runs
      .flatMap((run) => run.changeSets)
      .filter((set) => set.status === "COMPLETED")
      .map((set) => set.id)
      .sort()
      .join(",") ?? "";
  useEffect(() => {
    if (!committedChanges) return;
    for (const queryKey of [
      ["tasks"],
      ["views"],
      ["ai-references", projectId],
      ["dependencies"],
      ["ai-work", projectId],
      ["ai-graph", projectId],
    ]) {
      void client.invalidateQueries({ queryKey });
    }
  }, [committedChanges, projectId, client]);
  const activeRun = history.data?.runs.find((run) =>
    activeStatuses.has(run.status),
  );
  const canSteer =
    activeRun?.triggeredById === userId &&
    ["RUNNING", "QUEUED"].includes(activeRun.status);
  const activeRunId = activeRun?.id;
  const activeRunStatus = activeRun?.status;
  useEffect(() => {
    if (
      !activeRunId ||
      !activeRunStatus ||
      !["RUNNING", "QUEUED"].includes(activeRunStatus)
    )
      return;
    const stream = new EventSource(`/api/ai/runs/${activeRunId}/stream`, {
      withCredentials: true,
    });
    stream.addEventListener("progress", () => {
      void client.invalidateQueries({ queryKey: ["ai-chat", conversationId] });
    });
    stream.addEventListener("close", () => {
      stream.close();
      void client.invalidateQueries({ queryKey: ["ai-chat", conversationId] });
    });
    return () => stream.close();
  }, [activeRunId, activeRunStatus, conversationId, client]);
  const modelOptions = useMemo(
    () => [
      ...(providers.data?.connections ?? [])
        .filter((c) => c.enabled)
        .flatMap((c) =>
          c.modelConfigs
            .filter((m) => m.enabled)
            .map((m) => ({ id: m.id, label: `${c.label} · ${m.modelId}` })),
        ),
      ...(providers.data?.sharedModels ?? [])
        .filter(
          (model) =>
            !providers.data?.connections.some((connection) =>
              connection.modelConfigs.some((own) => own.id === model.id),
            ),
        )
        .map((model) => ({
          id: model.id,
          label: `${model.connectionLabel} · ${model.modelId} · ${language === "fr" ? "partagé par" : "shared by"} ${model.sharedByName}`,
        })),
    ],
    [providers.data, language],
  );
  useEffect(() => {
    setError("");
    requestId.current = null;
    atBottom.current = true;
  }, [conversationId]);
  useEffect(() => {
    if (
      initialPrompt?.nonce &&
      consumedPrompt.current !== initialPrompt.nonce
    ) {
      consumedPrompt.current = initialPrompt.nonce;
      updateDraft({
        content: initialPrompt.text,
        documentIds: initialPrompt.documentIds ?? [],
      });

      onConsumePrompt?.();
    }
  }, [initialPrompt, onConsumePrompt, updateDraft]);
  useEffect(() => {
    if (atBottom.current)
      endRef.current?.scrollIntoView({ behavior: "instant" });
  }, [history.data]);
  useEffect(() => {
    if (!providers.isLoading && !modelOptions.some((m) => m.id === modelId))
      setModelId(modelOptions[0]?.id ?? "");
  }, [modelOptions, modelId, providers.isLoading]);
  const refresh = () => {
    void client.invalidateQueries({ queryKey: ["ai-chat"] });
    void client.invalidateQueries({
      queryKey: ["ai-conversations", projectId],
    });
    void client.invalidateQueries({ queryKey: ["tasks"] });
  };
  const action = useMutation({
    mutationFn: async ({ path, body }: { path: string; body?: unknown }) =>
      apiPost(path, body ?? {}),
    onSuccess: refresh,
    onError: (err) => setError(err.message),
  });
  const send = useMutation({
    mutationFn: async (createdConversationId?: string) => {
      const content = draft;
      const selectedDocuments = documentIds;
      requestId.current ??= crypto.randomUUID();
      const messageRequestId = requestId.current;
      pendingSend.current = {
        content,
        documentIds: selectedDocuments,
        requestId: messageRequestId,
        draftKey,
      };
      let id = createdConversationId ?? conversationId;
      if (!id) {
        const created = await apiPost<{ conversation: Conversation }>(
          projectId
            ? `/ai/projects/${projectId}/conversations`
            : "/ai/assistant/conversations",
          {},
        );
        id = created.conversation.id;
      }
      if (!conversationId) {
        const nextKey = chatDraftKey(userId, projectId, id);
        saveChatDraft(nextKey, {
          content,
          documentIds: selectedDocuments,
          viewIds,
        });
        saveChatDraft(draftKey, { content: "", documentIds: [], viewIds: [] });
        pendingSend.current.draftKey = nextKey;
        onSelect(id);
      }
      requestId.current ??= crypto.randomUUID();
      if (canSteer && activeRun) {
        await apiPost(`/ai/runs/${activeRun.id}/steer`, {
          content,
          requestId: messageRequestId,
        });
        return;
      }
      await apiPost(`/ai/conversations/${id}/messages`, {
        content,
        requestId: messageRequestId,
        modelId,
        permissionMode,
        context: {
          ...context,
          viewIds,
          date: localCalendarDate(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        documentIds: selectedDocuments,
      });
    },
    onSuccess: () => {
      if (pendingSend.current)
        updateDraft(
          { content: "", documentIds: [], viewIds: [] },
          pendingSend.current.draftKey,
        );
      requestId.current = null;
      pendingSend.current = null;
      setError("");
      refresh();
    },
    onError: (err) => {
      setError(err.message);
      if (pendingSend.current) {
        updateDraft(
          {
            content: pendingSend.current.content,
            documentIds: pendingSend.current.documentIds,
          },
          pendingSend.current.draftKey,
        );
        requestId.current = pendingSend.current.requestId;
      }
    },
  });
  const statusLabel = (status: string) =>
    ({
      RUNNING: text.running,
      QUEUED: text.running,
      COMPLETED: text.completed,
      FAILED: text.failed,
      CANCELLED: text.cancelled,
      WAITING_FOR_APPROVAL: text.approval,
    })[status] || status;
  const renderProposal = (proposal: Proposal, run: Run) => (
    <div
      key={proposal.id}
      className="max-w-3xl mx-auto p-4 rounded-xl border border-primary/30 bg-primary/5 space-y-3"
    >
      <h4 className="font-semibold">
        {proposal.title} · {proposal.operations.length} {text.preview}
      </h4>
      {proposal.summary.privateSourceDisclosure && (
        <p className="text-sm text-amber-500">
          {language === "fr"
            ? "Ce plan peut publier dans le projet des informations issues de documents privés :"
            : "This plan may publish private document information in project tasks:"}{" "}
          {proposal.summary.privateSources
            ?.map((source) => source.name)
            .join(", ")}
        </p>
      )}
      {proposal.summary.destructive && (
        <p className="text-sm text-destructive">{text.destructive}</p>
      )}
      {proposal.operations.map((operation, index) => (
        <details key={operation.id} className="text-sm">
          <summary>
            {capabilityLabel(operation.payload.capability, language)} ·{" "}
            {proposal.summary.previews?.[index]?.entityTitle}
          </summary>
          {proposal.summary.previews?.[index] && (
            <ChangePreview
              preview={proposal.summary.previews[index]!}
              language={language}
            />
          )}
          <pre className="whitespace-pre-wrap break-words text-xs max-h-64 overflow-auto mt-2">
            {JSON.stringify(operation.payload.input, null, 2)}
          </pre>
        </details>
      ))}
      {proposal.status === "COMPLETED" ? (
        <div className="flex items-center gap-3 text-sm">
          <span className="text-green-500">{text.completed}</span>
        </div>
      ) : proposal.status === "FAILED" ? (
        <p className="text-destructive text-sm">
          {text.failed}
          {proposal.summary.failure?.message
            ? ` · ${proposal.summary.failure.message}`
            : proposal.summary.rolledBack
              ? " · rolled back"
              : ""}
        </p>
      ) : (
        run.triggeredById === userId &&
        chat?.canSend && (
          <div className="flex gap-2">
            <Button
              disabled={action.isPending}
              onClick={() =>
                action.mutate({
                  path: `/ai/changes/${proposal.id}/approve`,
                })
              }
            >
              {text.apply}
            </Button>
            <Button
              variant="outline"
              disabled={action.isPending}
              onClick={() =>
                action.mutate({ path: `/ai/runs/${run.id}/cancel` })
              }
            >
              {text.cancelled}
            </Button>
          </div>
        )
      )}
    </div>
  );
  const selectReference = (item: ChatReference, confirmed = false) => {
    if (
      (item.kind === "view" || item.kind === "document") &&
      !item.isShared &&
      chat?.visibility !== "PRIVATE" &&
      !confirmed
    ) {
      setPrivateReference(item);
      return;
    }
    if (item.kind === "view" || item.kind === "document")
      setViewIds((ids) => [...new Set([...ids, item.id])].slice(0, 10));
    if (item.kind === "document")
      setDocumentIds((ids) => [...new Set([...ids, item.id])].slice(0, 10));
    composer.current?.insert(item, referencesOpen);
    setContextPickerOpen(false);
    setReferencesOpen(false);
    setReferenceSearch("");
    setPrivateReference(null);
  };
  const events = [
    ...(history.data?.messages ?? [])
      .filter(
        (m) =>
          m.authorType !== "tool" &&
          m.content &&
          !(m.authorType === "user" && m.data.kind === "approval_result"),
      )
      .map((message) => ({
        id: message.id,
        date: message.createdAt,
        message,
        step: null as Step | null,
        proposal: null as Proposal | null,
        run: null as Run | null,
      })),
    ...(history.data?.runs ?? []).flatMap((run) =>
      run.steps
        .filter((step) => step.type !== "MODEL" || step.status === "RUNNING")
        .map((step) => ({
          id: step.id,
          date: step.startedAt,
          message: null as Message | null,
          step,
          proposal: null as Proposal | null,
          run,
        })),
    ),
    ...(history.data?.runs ?? []).flatMap((run) =>
      run.changeSets.map((proposal) => ({
        id: proposal.id,
        date:
          run.steps.find((step) => step.output?.changeSetId === proposal.id)
            ?.startedAt ?? proposal.createdAt,
        message: null as Message | null,
        step: null as Step | null,
        proposal,
        run,
      })),
    ),
    ...(history.data?.runs ?? [])
      .filter((run) => run.status === "FAILED")
      .map((run) => ({
        id: `error-${run.id}`,
        date: run.finishedAt ?? run.steps.at(-1)?.startedAt ?? run.createdAt,
        message: null as Message | null,
        step: null as Step | null,
        proposal: null as Proposal | null,
        run,
      })),
  ].sort((a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id));
  if (
    history.error &&
    "statusCode" in history.error &&
    [401, 403, 404].includes(Number(history.error.statusCode))
  ) {
    return (
      <p role="alert" className="p-6 text-destructive">
        {history.error.message}
      </p>
    );
  }
  return (
    <section
      className="relative flex flex-col flex-1 min-h-0 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md overflow-hidden"
      data-testid="ai-chat"
    >
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 shrink-0">
        <Bot className="h-5 w-5 text-primary" />
        <div className="min-w-0 flex-1">
          <h3 className="font-medium truncate">
            {chat?.title || text.newChat}
          </h3>
          {projectId && (
            <span className="text-xs text-foreground/70">
              {chat?.visibility === "PROJECT"
                ? text.project
                : chat?.visibility === "SHARED"
                  ? text.shared
                  : text.private}
            </span>
          )}
        </div>
        {(conversationId || projectId) && (
          <Button
            variant="ghost"
            size="icon"
            aria-label={language === "fr" ? "Historique" : "History"}
            onClick={() => setHistoryOpen(true)}
          >
            <HistoryIcon className="h-4 w-4" />
          </Button>
        )}
        {canCreate && (
          <Button
            variant="ghost"
            size="icon"
            aria-label={text.settings}
            onClick={() => {
              setSettingsTab("models");
              setSettingsOpen(true);
            }}
          >
            <Settings className="h-4 w-4" />
          </Button>
        )}
      </div>
      {history.data?.workflows?.map((workflow) => (
        <div
          key={workflow.id}
          role="status"
          className="border-b border-white/10 bg-primary/5 px-4 py-3 text-sm"
        >
          <p className="truncate font-medium" title={workflow.goal}>
            {workflow.goal}
          </p>
          <p className="mt-1 text-xs text-foreground-muted">
            {language === "fr"
              ? `${workflow.counts.committed} / ${workflow.counts.total} créées · ${workflow.counts.proposed} proposées · ${workflow.counts.pending} restantes`
              : `${workflow.counts.committed} / ${workflow.counts.total} committed · ${workflow.counts.proposed} proposed · ${workflow.counts.pending} remaining`}
          </p>
        </div>
      ))}
      <div
        className="flex-1 overflow-y-auto px-4 py-5 space-y-3"
        onScroll={(event) => {
          const el = event.currentTarget;
          atBottom.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 100;
        }}
      >
        {history.isLoading && (
          <p className="text-foreground/70">{text.loading}</p>
        )}
        {!events.length && !history.isLoading && (
          <div className="max-w-lg mx-auto text-center py-16 space-y-3">
            <Bot className="h-10 w-10 text-primary mx-auto" />
            <h3 className="text-xl font-semibold">
              {projectId
                ? text.empty
                : language === "fr"
                  ? "Comment puis-je vous aider ?"
                  : "How can I help?"}
            </h3>
            <p className="text-sm text-foreground/70">
              {projectId
                ? text.intro
                : language === "fr"
                  ? "Discutez de vos projets, capturez une tâche ou préparez votre semaine."
                  : "Discuss your projects, capture a task or plan your week."}
            </p>
          </div>
        )}
        {canCreate && projectId && (
          <WorkPlanningPanel
            projectId={projectId}
            onTask={onTask}
            onAsk={setDraft}
          />
        )}
        {events.map((event) =>
          event.message?.data.kind === "context_summary" ? (
            <div
              key={event.id}
              className="max-w-3xl mx-auto rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-foreground-muted"
            >
              <details>
                <summary className="cursor-pointer font-medium text-primary">
                  {language === "fr"
                    ? "Contexte résumé automatiquement"
                    : "Context automatically summarized"}{" "}
                  ·{" "}
                  {Number(event.message.data.estimatedBefore).toLocaleString(
                    language,
                  )}{" "}
                  →{" "}
                  {Number(event.message.data.estimatedAfter).toLocaleString(
                    language,
                  )}{" "}
                  tokens
                </summary>
                <p className="mt-3 whitespace-pre-wrap">
                  {event.message.content}
                </p>
              </details>
            </div>
          ) : event.message ? (
            <div
              key={event.id}
              className={cn(
                "max-w-3xl rounded-xl px-4 py-3",
                event.message.authorType === "user"
                  ? "ml-auto bg-primary/15 border border-primary/20"
                  : "mr-auto bg-white/5 border border-white/10",
              )}
            >
              <div className="text-xs text-foreground/70 mb-2 flex justify-between gap-3">
                {event.message.authorType === "user" ? text.you : "AI"}
                {canCreate && event.message.authorType === "assistant" && (
                  <button
                    aria-label="Save to memory"
                    onClick={() => {
                      setMemoryDraft(event.message!.content);
                      setSettingsTab("memory");
                      setSettingsOpen(true);
                    }}
                  >
                    <BookMarked className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="text-sm leading-6 break-words [&_pre]:overflow-x-auto [&_pre]:bg-black/20 [&_pre]:p-3 [&_pre]:rounded [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_table]:w-full [&_td]:border [&_td]:border-white/10 [&_td]:p-2 [&_th]:p-2">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  urlTransform={(url) =>
                    /^(task|member|view|document):[\w-]+$/.test(url)
                      ? url
                      : defaultUrlTransform(url)
                  }
                  components={{
                    img: () => null,
                    a: ({ href, children }) =>
                      /^(task|member|view|document):/.test(href ?? "") ? (
                        <button
                          className="inline-flex items-center gap-1 rounded-md border border-sky-400/20 bg-sky-400/10 px-1.5 text-sky-300 text-sm align-baseline"
                          onClick={() => {
                            const [kind, id] = (href ?? "").split(":");
                            if (kind === "task") onTask(id!);
                            else if (kind === "view" || kind === "document")
                              onView(id!);
                          }}
                        >
                          <span>{children}</span>
                        </button>
                      ) : (
                        <a
                          href={href}
                          onClick={(e) => {
                            if (!href) return;
                            const url = new URL(href, location.origin);
                            if (
                              url.origin === location.origin &&
                              url.searchParams.get("task")
                            ) {
                              e.preventDefault();
                              onTask(url.searchParams.get("task")!);
                            } else if (
                              url.origin === location.origin &&
                              url.searchParams.get("view")
                            ) {
                              e.preventDefault();
                              onView(url.searchParams.get("view")!);
                            }
                          }}
                          rel="noopener noreferrer"
                        >
                          {children}
                        </a>
                      ),
                  }}
                >
                  {event.message.content.replace(
                    /@\[([^\]]+)\]\((task|member|view|document):([^\s)]+)\)/g,
                    "[$1]($2:$3)",
                  )}
                </ReactMarkdown>
              </div>
            </div>
          ) : event.run && !event.step && !event.proposal ? (
            <div
              key={event.id}
              role="alert"
              className="max-w-3xl mx-auto rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive"
            >
              {event.run.error?.message || text.failed}
            </div>
          ) : event.proposal && event.run ? (
            renderProposal(event.proposal, event.run)
          ) : (
            event.step && (
              <div
                key={event.id}
                className="max-w-3xl mx-auto rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm"
              >
                <div className="flex gap-2 items-center">
                  {event.step.status === "RUNNING" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                  ) : event.step.status === "FAILED" ? (
                    <X className="h-3.5 w-3.5 text-destructive" />
                  ) : (
                    <Check className="h-3.5 w-3.5 text-green-500" />
                  )}
                  <span className="font-medium">
                    {event.step.output?.approvalRequired
                      ? text.preview
                      : event.step.type === "MODEL" &&
                          event.step.capability !== "compact_context"
                        ? language === "fr"
                          ? "L’IA prépare sa réponse…"
                          : "AI is preparing a response…"
                        : capabilityLabel(event.step.capability, language)}
                  </span>
                  <span className="text-xs text-foreground/70 ml-auto">
                    {statusLabel(
                      event.step.output?.approvalRequired
                        ? history.data?.runs
                            .flatMap((run) => run.changeSets)
                            .find(
                              (proposal) =>
                                proposal.id === event.step!.output!.changeSetId,
                            )?.status === "PROPOSED"
                          ? "WAITING_FOR_APPROVAL"
                          : (history.data?.runs
                              .flatMap((run) => run.changeSets)
                              .find(
                                (proposal) =>
                                  proposal.id ===
                                  event.step!.output!.changeSetId,
                              )?.status ?? event.step.status)
                        : event.step.status,
                    )}
                  </span>
                </div>
                {event.step.error && (
                  <p role="alert" className="text-destructive mt-1">
                    {event.step.error.message}
                  </p>
                )}
                {Array.isArray(event.step.output?.operations) &&
                  (
                    event.step.output.operations as {
                      result?: {
                        after?: { task?: { id: string; title: string } };
                      };
                    }[]
                  ).map(
                    (op, index) =>
                      op.result?.after?.task && (
                        <button
                          key={index}
                          className="block text-primary text-xs mt-1"
                          onClick={() => onTask(op.result!.after!.task!.id)}
                        >
                          {op.result.after.task.title}
                        </button>
                      ),
                  )}
                {typeof event.step.input?.taskId === "string" && (
                  <button
                    className="text-xs text-primary mt-1"
                    onClick={() => onTask(event.step!.input!.taskId as string)}
                  >
                    {text.openTask}
                  </button>
                )}
                {Boolean(event.step.output?.documentChange) &&
                  (() => {
                    const change = event.step!.output!.documentChange as {
                      id: string;
                      viewId: string;
                      name: string;
                      status?: string;
                    };
                    return (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onView(change.viewId)}
                        >
                          {change.name}
                        </Button>
                      </div>
                    );
                  })()}
                {Boolean(event.step.output?.proposal) && (
                  <div className="mt-2">
                    <p>{text.documentEdit}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const proposal = event.step!.output!.proposal as Record<
                          string,
                          unknown
                        >;
                        onDocumentEdit(proposal.viewId as string, {
                          ...proposal,
                          before: event.step!.output!.before,
                        });
                      }}
                    >
                      {text.openEditor}
                    </Button>
                  </div>
                )}
                {event.step.output?.questionRequired === true && event.run && (
                  <AgentQuestion
                    runId={event.run.id}
                    stepId={event.step.id}
                    question={String(event.step.output.question)}
                    options={event.step.output.options as string[] | undefined}
                    answer={event.step.output.answer as string | undefined}
                    canAnswer={
                      event.run.status === "WAITING_FOR_INPUT" &&
                      event.step.status === "COMPLETED" &&
                      event.run.steps
                        .filter(
                          (step) =>
                            step.capability === "ask_user" &&
                            step.status === "COMPLETED",
                        )
                        .at(-1)?.id === event.step.id &&
                      event.run.triggeredById === userId &&
                      !!chat?.canSend
                    }
                    onAnswer={refresh}
                  />
                )}
                {event.step.input?.restricted === true ||
                event.step.output?.restricted === true ? (
                  <p className="mt-2 text-xs text-foreground-muted">
                    {language === "fr"
                      ? "Détails masqués : l’accès à une ressource a changé."
                      : "Details hidden: access to a resource has changed."}
                  </p>
                ) : (
                  (event.step.input !== null || event.step.output !== null) && (
                    <details className="mt-1 text-xs text-foreground/70">
                      <summary className="cursor-pointer flex gap-1 items-center">
                        <ChevronDown className="h-3 w-3" />
                        {text.details}
                      </summary>
                      <pre className="max-h-64 overflow-auto whitespace-pre-wrap mt-2">
                        {JSON.stringify(
                          {
                            input: event.step.input,
                            output: event.step.output,
                          },
                          null,
                          2,
                        )}
                      </pre>
                    </details>
                  )
                )}
              </div>
            )
          ),
        )}
        {history.data?.runs
          .filter((run) => run.status === "WAITING_FOR_INPUT")
          .flatMap((run) =>
            run.steps
              .filter((step) => step.output?.access_required)
              .map((step) => (
                <div
                  key={`consent-${step.id}`}
                  className="max-w-3xl mx-auto p-4 rounded-xl border border-primary/30 bg-primary/5 space-y-3"
                >
                  <h4 className="font-semibold">
                    {text.attach} ·{" "}
                    {String(step.output?.name ?? step.output?.viewId ?? "")}
                  </h4>

                  {run.triggeredById === userId && (chat?.canSend || (canCreate && run.trigger === "mcp")) && (
                    <div className="flex gap-2">
                      <Button
                        disabled={action.isPending}
                        onClick={() =>
                          setGrantRequest({
                            runId: run.id,
                            viewId: String(step.output!.viewId),
                            name: String(step.output?.name ?? "Document"),
                          })
                        }
                      >
                        {text.consentTitle}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() =>
                          action.mutate({ path: `/ai/runs/${run.id}/cancel` })
                        }
                      >
                        {text.deny}
                      </Button>
                    </div>
                  )}
                </div>
              )),
          )}
        <div ref={endRef} />
      </div>
      {(error || history.error) && (
        <div role="alert" className="px-4 py-2 text-sm text-destructive">
          {error || history.error?.message}
        </div>
      )}
      <div className="border-t border-white/10 p-3 bg-white/[0.02] shrink-0 relative">
        {canCreate && (chat?.canSend ?? true) ? (
          <>
            {!providers.isLoading && !modelOptions.length && <p className="mb-3 text-sm text-foreground-secondary">{language === "fr" ? "Pour discuter avec l’assistant, ajoutez un modèle IA personnel ou utilisez un modèle partagé du projet. " : "To chat with the Assistant, add a personal AI model or use a model shared with this project. "}<Link className="text-primary underline" to="/ai/setup">{language === "fr" ? "Configurer un modèle gratuit" : "Set up a free model"}</Link></p>}
            <div className="flex items-center gap-2 mb-2 flex-wrap px-1">
              <Select
                value={modelId || undefined}
                onOpenChange={(open) => {
                  if (open)
                    void client.invalidateQueries({
                      queryKey: ["ai-project-models", projectId],
                    });
                }}
                onValueChange={(value) => {
                  if (value === "taskflow:add-model") { navigate("/settings#ai"); return; }
                  setModelId(value);
                  localStorage.setItem("taskflow:ai:model", value);
                }}
              >
                <SelectTrigger
                  aria-label={text.model}
                  className="h-8 w-auto min-w-0 max-w-[min(24rem,65vw)] rounded-lg bg-white/[0.03] border-white/10 px-2.5 text-xs gap-2"
                >
                  <Bot className="h-4 w-4 text-primary shrink-0" />
                  <SelectValue placeholder={text.noModel}>
                    {modelOptions.find((model) => model.id === modelId)?.label}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-80 max-w-[min(32rem,90vw)]">
                  <SelectGroup>
                    <SelectLabel>
                      {language === "fr"
                        ? "Mes modèles et modèles partagés"
                        : "My models and shared models"}
                    </SelectLabel>
                    {modelOptions.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        <span className="inline-flex items-center gap-2">
                          <span className="truncate">{model.label}</span>
                          {/free/i.test(model.label) && (
                            <span className="rounded bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 text-[10px]">
                              {language === "fr" ? "Gratuit" : "Free"}
                            </span>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                    <SelectItem value="taskflow:add-model"><span className="inline-flex items-center gap-2"><Plus className="h-4 w-4" />{language === "fr" ? "Ajouter un modèle" : "Add a model"}</span></SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <PermissionSelector
                value={permissionMode}
                onChange={changePermission}
              />
              <span
                className="text-xs text-foreground/70 ml-auto"
                title={
                  language === "fr"
                    ? "Les compteurs additionnent tous les appels au modèle. Le cache fait partie des tokens d’entrée, ce n’est pas une quantité supplémentaire."
                    : "Counts sum model calls. Cached input is included in input tokens, not added to them."
                }
              >
                {language === "fr" ? "Dernière requête" : "Last request"}:{" "}
                {(
                  (history.data?.runs[0]?.promptTokens ?? 0) +
                  (history.data?.runs[0]?.completionTokens ?? 0)
                ).toLocaleString(language)}{" "}
                {text.tokens}
                <details className="inline ml-2">
                  <summary className="inline cursor-pointer underline">
                    {language === "fr" ? "Usage total" : "Total usage"}
                  </summary>
                  <div className="mt-1">
                    {language === "fr" ? "Entrée" : "Input"}:{" "}
                    {(history.data?.usage?.inputTokens ?? 0).toLocaleString(
                      language,
                    )}{" "}
                    · {language === "fr" ? "dont en cache" : "of which cached"}:{" "}
                    {(
                      history.data?.usage?.cachedInputTokens ?? 0
                    ).toLocaleString(language)}{" "}
                    · {language === "fr" ? "Sortie" : "Output"}:{" "}
                    {(history.data?.usage?.outputTokens ?? 0).toLocaleString(
                      language,
                    )}
                  </div>
                </details>
              </span>
            </div>
            {!!viewIds.length && (
              <div className="flex flex-wrap gap-2 mb-2">
                {viewIds.map((id) => {
                  const view = references.data?.views.find(
                    (view) => view.id === id,
                  );
                  return view ? (
                    <span
                      key={id}
                      className="inline-flex max-w-full items-center gap-2 rounded-lg border border-primary/20 bg-primary/10 px-2 py-1 text-xs"
                    >
                      <ViewBadge view={view} />
                      <button
                        aria-label={
                          language === "fr" ? "Retirer la vue" : "Remove view"
                        }
                        onClick={() => {
                          setViewIds((ids) =>
                            ids.filter((item) => item !== id),
                          );
                          setDocumentIds((ids) =>
                            ids.filter((item) => item !== id),
                          );
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            )}
            {!modelOptions.length && (
              <button
                className="text-sm text-primary"
                onClick={() => setSettingsOpen(true)}
              >
                {text.noModel}
              </button>
            )}
            <div className="flex items-end gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-2.5 focus-within:border-primary/40 transition-colors">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 rounded-lg"
                aria-label={
                  language === "fr" ? "Ajouter du contexte" : "Add context"
                }
                onClick={() => {
                  setReferenceSearch("");
                  setContextPickerOpen(true);
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
              <ReferenceComposer
                ref={composer}
                value={draft}
                onChange={setDraft}
                onMention={(query, anchor) => {
                  setReferencesOpen(query !== null);
                  setReferenceSearch(query ?? "");
                  setMentionAnchor(anchor ?? null);
                }}
                onMentionKey={(event) =>
                  inlinePicker.current?.handleKey(event) ?? false
                }
                placeholder={
                  canSteer
                    ? language === "fr"
                      ? "Donnez une nouvelle consigne pendant que l’IA travaille…"
                      : "Send a new instruction while the AI works…"
                    : projectId
                      ? text.placeholder
                      : language === "fr"
                        ? "Écrivez à votre Assistant…"
                        : "Message your Assistant…"
                }
                disabled={send.isPending || (!!activeRun && !canSteer)}
                onSend={() => {
                  if (
                    draft.trim() &&
                    !send.isPending &&
                    (!activeRun || canSteer) &&
                    (modelOptions.length || canSteer)
                  ) {
                    if (projectId && !conversationId) setCreationOpen(true);
                    else send.mutate(undefined);
                  }
                }}
              />
              {activeRun?.triggeredById === userId ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 rounded-lg"
                  aria-label={text.stop}
                  disabled={action.isPending}
                  onClick={() =>
                    action.mutate({ path: `/ai/runs/${activeRun.id}/cancel` })
                  }
                >
                  <CircleStop className="h-4 w-4" />
                </Button>
              ) : null}
              <Button
                size="icon"
                className="h-8 w-8 shrink-0 rounded-lg"
                aria-label={text.send}
                disabled={
                  !draft.trim() ||
                  send.isPending ||
                  (!!activeRun && !canSteer) ||
                  (!modelOptions.length && !canSteer)
                }
                onClick={() => {
                  if (projectId && !conversationId) setCreationOpen(true);
                    else send.mutate(undefined);
                }}
              >
                {send.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </>
        ) : (
          <p className="text-sm text-foreground/70">{text.readOnly}</p>
        )}
      </div>
      {referencesOpen &&
        mentionAnchor &&
        createPortal(
          <div
            style={(() => {
              const viewport = window.visualViewport;
              const width = viewport?.width ?? innerWidth;
              const top = viewport?.offsetTop ?? 0;
              const bottom = top + (viewport?.height ?? innerHeight);
              const availableAbove = mentionAnchor.top - top - 12;
              const availableBelow = bottom - mentionAnchor.bottom - 12;
              const above =
                availableAbove >= 120 || availableAbove >= availableBelow;
              const height = Math.max(
                40,
                Math.min(288, above ? availableAbove : availableBelow),
              );
              return {
                position: "fixed" as const,
                left: Math.max(
                  8,
                  Math.min(
                    mentionAnchor.left,
                    width - Math.min(520, width - 16) - 8,
                  ),
                ),
                width: Math.min(520, width - 16),
                height,
                ...(above
                  ? { top: Math.max(top + 6, mentionAnchor.top - height - 6) }
                  : { top: mentionAnchor.bottom + 6 }),
              };
            })()}
            className="z-50 flex overflow-hidden rounded-xl border border-white/15 bg-surface shadow-2xl"
          >
            <ReferencePicker
              ref={inlinePicker}
              inline
              data={mentionQuery.data ?? references.data}
              search={referenceSearch}
              onSearch={setReferenceSearch}
              onSelect={selectReference}
              selectedIds={viewIds}
            />
          </div>,
          document.body,
        )}
      {creationOpen && projectId && (
        <ProjectChatCreationDialog projectId={projectId} references={references.data} onClose={() => setCreationOpen(false)} onCreated={(id) => send.mutate(id)} />
      )}
      <Dialog open={contextPickerOpen} onOpenChange={setContextPickerOpen}>
        <DialogContent className="max-w-2xl w-[calc(100%-2rem)] gap-0 overflow-hidden rounded-2xl p-0">
          <DialogHeader className="px-5 py-4 border-b border-white/10">
            <DialogTitle>
              {language === "fr" ? "Ajouter du contexte" : "Add context"}
            </DialogTitle>
          </DialogHeader>
          <ReferencePicker
            data={mentionQuery.data ?? references.data}
            search={referenceSearch}
            onSearch={setReferenceSearch}
            onSelect={selectReference}
            selectedIds={viewIds}
          />
        </DialogContent>
      </Dialog>
      {(conversationId || projectId) && (
        <AiHistoryDialog
          conversationId={conversationId || undefined}
          projectId={projectId || undefined}
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      <ChatSettings
        projectId={projectId}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        canManage={canManage}
        initialTab={settingsTab}
        initialMemory={memoryDraft}
        permissionMode={permissionMode}
        onPermission={changePermission}
        onTask={onTask}
        onView={onView}
      />
      <Dialog
        open={!!privateReference || !!grantRequest}
        onOpenChange={(value) => {
          if (!value) {
            setPrivateReference(null);
            setGrantRequest(null);
          }
        }}
      >
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>
              {language === "fr"
                ? "Partager un contenu privé avec l’IA"
                : "Share private content with AI"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground-secondary">
            {language === "fr"
              ? `« ${privateReference?.label ?? grantRequest?.name} » sera envoyé au modèle sélectionné. Son contenu et les réponses seront visibles par les lecteurs de cette conversation.`
              : `“${privateReference?.label ?? grantRequest?.name}” will be sent to the selected model. Its content and responses will be visible to this conversation’s readers.`}
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setPrivateReference(null);
                setGrantRequest(null);
              }}
            >
              {language === "fr" ? "Annuler" : "Cancel"}
            </Button>
            <Button
              onClick={() => {
                if (privateReference) selectReference(privateReference, true);
                if (grantRequest) {
                  action.mutate({
                    path: `/ai/runs/${grantRequest.runId}/grants`,
                    body: {
                      viewId: grantRequest.viewId,
                      permitDisclosure: true,
                    },
                  });
                  setGrantRequest(null);
                }
              }}
            >
              {language === "fr"
                ? "Autoriser et continuer"
                : "Allow and continue"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function capabilityLabel(capability: string | null, language: "fr" | "en") {
  const names: Record<string, [string, string]> = {
    model: ["Réponse du modèle", "Model response"],
    get_task: ["Lecture d’une tâche", "Read task"],
    get_task_context: ["Lecture du contexte de tâche", "Read task context"],
    search_tasks: ["Recherche de tâches", "Search tasks"],
    get_skill: ["Guide de l’application", "Application guide"],
    ask_user: ["Une précision est nécessaire", "Clarification needed"],
    get_custom_field: ["Lecture d’un champ personnalisé", "Read custom field"],
    create_custom_field: [
      "Création d’un champ personnalisé",
      "Create custom field",
    ],
    update_custom_field: [
      "Modification d’un champ personnalisé",
      "Update custom field",
    ],
    delete_custom_field: [
      "Suppression d’un champ personnalisé",
      "Delete custom field",
    ],
    list_memories: ["Lecture de la mémoire", "Read memory"],
    save_memory: ["Mise à jour de la mémoire", "Update memory"],
    delete_memory: ["Suppression d’une mémoire", "Delete memory"],
    compact_context: ["Compaction du contexte", "Context compaction"],
    get_document: ["Lecture d’un document", "Read document"],
    create_task: ["Création d’une tâche", "Create task"],
    update_task: ["Modification d’une tâche", "Edit task"],
    propose_changes: ["Préparation d’une proposition", "Prepare proposal"],
    add_comment: ["Ajout d’un commentaire", "Add comment"],
    add_dependency: ["Ajout d’une dépendance", "Add dependency"],
    archive_task: ["Archivage d’une tâche", "Archive task"],
    delete_task: ["Suppression d’une tâche", "Delete task"],
    plan_task_for_day: ["Planification d’une tâche", "Plan task"],
    get_my_work: ["Analyse de mon travail", "Read my work"],
    propose_document_edit: [
      "Proposition de modification du document",
      "Propose document edit",
    ],
  };
  return (
    names[capability ?? ""]?.[language === "fr" ? 0 : 1] ??
    capability?.replace(/_/g, " ") ??
    "AI"
  );
}

function ChangePreview({
  preview,
  language,
}: {
  preview: NonNullable<Proposal["summary"]["previews"]>[number];
  language: "fr" | "en";
}) {
  const fields = Object.entries(preview.after).filter(
    ([key]) =>
      !["expectedVersion", "sourceDocumentIds", "taskId", "parentId"].includes(
        key,
      ),
  );
  return (
    <div className="my-2 text-xs space-y-1">
      {preview.stale && (
        <p className="text-destructive">
          {language === "fr"
            ? "La tâche a changé. Une nouvelle proposition est nécessaire."
            : "Task changed. Prepare a new proposal."}
        </p>
      )}
      {fields.map(([key, value]) => (
        <div
          key={key}
          className="grid grid-cols-3 gap-2 border-b border-white/10 py-1"
        >
          <span>
            {(
              {
                title: "Titre",
                description: "Description",
                status: "Statut",
                priority: "Priorité",
                date: "Date prévue",
                plannedMinutes: "Minutes prévues",
                dueDate: "Échéance",
              } as Record<string, string>
            )[key] && language === "fr"
              ? (
                  {
                    title: "Titre",
                    description: "Description",
                    status: "Statut",
                    priority: "Priorité",
                    date: "Date prévue",
                    plannedMinutes: "Minutes prévues",
                    dueDate: "Échéance",
                  } as Record<string, string>
                )[key]
              : key}
          </span>
          <span className="text-foreground/70 break-words">
            {preview.before?.[key] === undefined
              ? "—"
              : typeof preview.before[key] === "string"
                ? String(preview.before[key])
                : JSON.stringify(preview.before[key])}
          </span>
          <span className="break-words text-primary">
            {typeof value === "string" ? value : JSON.stringify(value)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function PersonalProviderDialog({
  open,
  onClose,
  connectionId,
}: {
  open: boolean;
  onClose(): void;
  userId?: string;
  connectionId?: string;
}) {
  const text = useText();
  const fr = useI18n().language === "fr";
  const client = useQueryClient();
  const [id, setId] = useState<string>();
  const initialized = useRef<string | null>(null);
  const [provider, setProvider] = useState("openrouter");
  const [key, setKey] = useState("");
  const [models, setModels] = useState("");
  const [label, setLabel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const settings = useQuery({
    queryKey: ["ai-providers"],
    queryFn: () => apiGet<Providers>("/ai/providers"),
    enabled: open,
  });
  const catalog = useMutation({
    mutationFn: () => id && !key && settings.data?.connections.find(connection => connection.id === id)?.baseUrl === baseUrl && settings.data?.connections.find(connection => connection.id === id)?.provider === provider
      ? apiGet<{ models: { id: string; name: string; toolCalling: boolean | null; free: boolean | null }[] }>(`/ai/providers/${id}/models?refresh=true`)
      : apiPost<{ models: { id: string; name: string; toolCalling: boolean | null; free: boolean | null }[] }>("/ai/providers/discover", {
      ...(id ? { id } : {}), provider, ...(key ? { apiKey: key } : {}), ...(provider === "custom" ? { baseUrl } : {}),
    }),
  });
  const [freeOnly, setFreeOnly] = useState(false);
  const [search, setSearch] = useState("");
  const save = useMutation({
    mutationFn: () =>
      apiPost("/ai/providers", {
        id,
        provider,
        ...(key ? { apiKey: key } : {}),
        ...(provider === "custom" ? { baseUrl } : {}),
        label,
        enabled: true,
        modelIds: models
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      }),
    onSuccess: () => {
      setKey("");
      void client.invalidateQueries({ queryKey: ["ai-providers"] });
      void client.invalidateQueries({ queryKey: ["ai-project-models"] });
      onClose();
    },
  });
  const resetCatalog = catalog.reset;
  const resetMutation = save.reset;
  const reset = useCallback(() => {
    setId(undefined);
    setKey("");
    setModels("");
    setLabel("");
    setBaseUrl("");
    resetMutation();
  }, [resetMutation]);
  useEffect(() => {
    if (!open) { initialized.current = null; reset(); resetCatalog(); setSearch(""); setFreeOnly(false); }
  }, [open, reset, resetCatalog]);
  useEffect(() => {
    if (!open) return;
    if (!connectionId) {
      reset();
      return;
    }
    const connection = settings.data?.connections.find(
      (item) => item.id === connectionId,
    );
    if (!connection || initialized.current === connection.id) return;
    initialized.current = connection.id;
    setId(connection.id);
    setLabel(connection.label);
    setKey("");
    setModels(
      connection.modelConfigs
        .filter((model) => model.enabled)
        .map((model) => model.modelId)
        .join(", "),
    );
    setBaseUrl(connection.baseUrl);
    setProvider(
      connection.provider ?? (connection.baseUrl.includes("openrouter.ai/")
        ? "openrouter"
        : connection.baseUrl.includes("/zen/go/")
          ? "opencode-go"
          : connection.baseUrl.includes("opencode.ai/zen/")
            ? "opencode-zen"
            : "custom"),
    );
  }, [open, connectionId, settings.data, reset]);
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-auto">
        <DialogHeader>
          <DialogTitle>
            {connectionId
              ? fr
                ? "Modifier le fournisseur"
                : "Edit provider"
              : fr
                ? "Ajouter un fournisseur"
                : "Add provider"}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <label className="block text-sm font-medium">
            {fr ? "Fournisseur" : "Provider"}
          </label>
          <Select value={provider} onValueChange={(value) => { setProvider(value); setModels(""); setKey(""); setFreeOnly(false); catalog.reset(); }}>
            <SelectTrigger aria-label="Provider">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries({ openai: "OpenAI", anthropic: "Anthropic", google: "Google Gemini", groq: "Groq", mistral: "Mistral AI", deepseek: "DeepSeek", xai: "xAI", together: "Together AI" }).map(([value, name]) => <SelectItem key={value} value={value}>{name}</SelectItem>)}
              <SelectItem value="openrouter">OpenRouter</SelectItem>
              <SelectItem value="opencode-go">OpenCode Go</SelectItem>
              <SelectItem value="opencode-zen">OpenCode Zen</SelectItem>
              <SelectItem value="custom">
                {fr
                  ? "Compatible OpenAI · personnalisé"
                  : "Custom · OpenAI compatible"}
              </SelectItem>
            </SelectContent>
          </Select>
          {provider === "custom" && (
            <label className="block text-sm">
              URL HTTPS
              <input
                type="url"
                required
                className="w-full px-3 py-2 mt-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                value={baseUrl}
                onChange={(e) => { setBaseUrl(e.target.value); catalog.reset(); }}
                placeholder="https://ai.company.com/v1"
              />
            </label>
          )}
          <label className="block text-sm">
            {text.title}
            <input
              className="w-full px-3 py-2 mt-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            {text.key}
            {id
              ? ` (${fr ? "laisser vide pour conserver" : "leave blank to keep"})`
              : ""}
            <input
              required={!id}
              autoComplete="off"
              type="password"
              className="w-full px-3 py-2 mt-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
              value={key}
              onChange={(e) => { setKey(e.target.value); catalog.reset(); }}
            />
          </label>
          <Button type="button" variant="outline" disabled={catalog.isPending || (connectionId && !id) || (!key && !id) || (provider === "custom" && !baseUrl)} onClick={() => catalog.mutate()}>
            {catalog.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {fr ? "Charger / actualiser les modèles disponibles" : "Load / refresh available models"}
          </Button>
          {id && !key && <p className="text-xs text-foreground-muted">{fr ? "Votre clé enregistrée est utilisée sur le serveur. Il est inutile de la saisir à nouveau pour actualiser les modèles." : "Your saved key is used on the server. You do not need to enter it again to refresh models."}</p>}
          <p className="text-xs text-foreground-muted">{fr ? "Le catalogue est récupéré chez le fournisseur avec votre clé. La disponibilité reste soumise à votre compte et à ses quotas." : "The catalog is fetched from the provider using your key. Availability depends on your account and quotas."}</p>
          <label className="block text-sm">
            {text.models}
            <input
              required
              className="w-full px-3 py-2 mt-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
              value={models}
              onChange={(e) => setModels(e.target.value)}
              placeholder={fr ? "Choisissez dans le catalogue, ou saisissez les identifiants" : "Choose from the catalog, or enter model IDs"}
            />
          </label>
          {!!catalog.data?.models.length && (
            <div className="text-sm">
              <p>{fr ? "Catalogue des modèles" : "Model catalog"} · {catalog.data.models.length}</p>
              {provider === "openrouter" && <label className="flex items-center gap-2 my-2"><Checkbox checked={freeOnly} onCheckedChange={(value) => setFreeOnly(value === true)} />{fr ? "Modèles gratuits uniquement" : "Free models only"}</label>}
              <input
                className="w-full p-2 bg-surface border rounded my-2"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search models"
              />
              <div className="max-h-40 overflow-auto">
                {catalog.data.models
                  .filter((model) =>
                    (!freeOnly || model.free === true) && (model.name + " " + model.id).toLowerCase().includes(search.toLowerCase()),
                  )
                  .slice(0, 100)
                  .map((model) => (
                    <label
                      key={model.id}
                      className="flex items-center gap-2 py-1"
                    >
                      <Checkbox
                        disabled={model.toolCalling === false}
                        checked={models
                          .split(",")
                          .map((s) => s.trim())
                          .includes(model.id)}
                        onCheckedChange={(checked) => {
                          const selected = models
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean);
                          setModels(
                            (checked
                              ? [...new Set([...selected, model.id])]
                              : selected.filter((id) => id !== model.id)
                            ).join(", "),
                          );
                        }}
                      />
                      {model.name}{model.free === true ? (fr ? " · Gratuit" : " · Free") : ""}
                      {model.toolCalling === false ? " · chat only" : ""}
                    </label>
                  ))}
              </div>
            </div>
          )}
          {catalog.isSuccess && !catalog.data.models.length && <p className="text-sm">{fr ? "Aucun modèle disponible retourné." : "No available models returned."}</p>}
          {catalog.error && (
            <p className="text-xs text-foreground/70">
              {catalog.error.message}
            </p>
          )}
          {save.error && (
            <p role="alert" className="text-sm text-destructive">
              {save.error?.message}
            </p>
          )}
          <Button type="submit" disabled={save.isPending}>
            {text.save}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SharingDialog({
  open,
  onClose,
  chat,
  references,
  onSaved,
  onRemoved,
}: {
  open: boolean;
  onClose(): void;
  chat: Conversation;
  references?: References;
  onSaved(): void;
  onRemoved?(): void;
}) {
  const userId = useAuthStore((state) => state.user?.id);
  const text = useText();
  const fr = useI18n().language === "fr";
  const [title, setTitle] = useState(chat.title || "");
  const [visibility, setVisibility] = useState(chat.visibility);
  const [members, setMembers] = useState(
    chat.members?.map((m) => m.userId) ?? [],
  );
  const save = useMutation({
    mutationFn: () =>
      apiPatch(`/ai/conversations/${chat.id}`, {
        expectedVersion: chat.version,
        title,
        visibility,
        memberIds: members,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => apiDelete(`/ai/conversations/${chat.id}`),
    onSuccess: () => {
      if (userId) {
        try {
          for (const key of Object.keys(localStorage)) {
            if (
              key.startsWith(`taskflow:ai:draft:${userId}:`) &&
              key.endsWith(`:${chat.id}`)
            )
              localStorage.removeItem(key);
          }
        } catch {
          /* Browser storage may be disabled. */
        }
      }
      onRemoved?.();
      onSaved();
      onClose();
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {fr ? "Modifier la conversation" : "Edit conversation"}
          </DialogTitle>
        </DialogHeader>
        <label className="text-sm">
          {text.title}
          <input
            className="w-full px-3 py-2 mt-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        {chat.scope !== "PERSONAL" && (
          <ChatPrivacyFields
            visibility={visibility}
            onVisibility={setVisibility}
            memberIds={members}
            onMembers={setMembers}
            members={
              references?.members.filter(
                (member) => member.userId !== chat.createdById,
              ) ?? []
            }
          />
        )}
        {(save.error || remove.error) && (
          <p role="alert" className="text-sm text-destructive">
            {(save.error || remove.error)?.message}
          </p>
        )}
        <div className="flex justify-between">
          <Button
            variant="destructive"
            size="sm"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm(text.confirmDelete)) remove.mutate();
            }}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {text.delete}
          </Button>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            {text.save}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ProjectChatCreationDialog({ projectId, references, onClose, onCreated }: {
  projectId: string; references?: References; onClose(): void; onCreated(id: string): void;
}) {
  const fr = useI18n().language === "fr";
  const userId = useAuthStore(state => state.user?.id);
  const [visibility, setVisibility] = useState<"PRIVATE" | "SHARED" | "PROJECT">("PRIVATE");
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const create = useMutation({
    mutationFn: () => apiPost<{ conversation: Conversation }>(`/ai/projects/${projectId}/conversations`, { visibility, memberIds: visibility === "SHARED" ? memberIds : [] }),
    onSuccess: ({ conversation }) => { onCreated(conversation.id); onClose(); },
  });
  return <Dialog open onOpenChange={open => { if (!open && !create.isPending) onClose(); }}>
    <DialogContent className="max-w-lg rounded-2xl">
      <DialogHeader><DialogTitle>{fr ? "Visibilité de la conversation" : "Conversation visibility"}</DialogTitle></DialogHeader>
      <p className="text-sm text-foreground-muted">{fr ? "Choisissez qui peut lire cette conversation. Le titre sera créé à partir de votre premier message." : "Choose who can read this conversation. Its title comes from your first message."}</p>
      <ChatPrivacyFields visibility={visibility} onVisibility={setVisibility} memberIds={memberIds} onMembers={setMemberIds} members={(references?.members ?? []).filter(member => member.userId !== userId)} />
      {create.error && <p role="alert" className="text-sm text-destructive">{create.error.message}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" disabled={create.isPending} onClick={onClose}>{fr ? "Annuler" : "Cancel"}</Button>
        <Button disabled={create.isPending || (visibility === "SHARED" && !memberIds.length) || !references} onClick={() => create.mutate()}>{create.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{fr ? "Créer" : "Create"}</Button>
      </div>
    </DialogContent>
  </Dialog>;
}
